import { ArrowLeft, Shield } from "lucide-react";
import { useState } from "react";

import { AppealModal } from "@/components/appeal-modal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { InfoBox } from "@/components/ui/info-box";
import { SectionHeader } from "@/components/ui/section-header";
import { ViolationList } from "@/components/ui/violation-list";
import type { CaseNotice, DomainNotice, SearchType } from "@/lib/types";

interface NoticeDetailProps {
  notice: CaseNotice | DomainNotice | null;
  searchType: SearchType;
  onBack: () => void;
}

export function NoticeDetail({
  notice,
  searchType,
  onBack,
}: NoticeDetailProps) {
  const [showAppealModal, setShowAppealModal] = useState(false);

  if (!notice) {
    return (
      <EmptyState
        title="No results found"
        message={`The ${searchType === "case" ? "case number" : "domain"} you searched for could not be found.`}
        actionLabel="Back to Search"
        onAction={onBack}
      />
    );
  }

  const isDomainNotice = (n: CaseNotice | DomainNotice): n is DomainNotice => {
    return "domain" in n;
  };

  const isCaseNotice = (n: CaseNotice | DomainNotice): n is CaseNotice => {
    return "caseNumber" in n && !("domain" in n);
  };

  const getSubtitle = () => {
    if (searchType === "case" && isCaseNotice(notice)) {
      return `Case: ${notice.caseNumber}`;
    }
    if (isDomainNotice(notice)) {
      return `Domain: ${notice.domain}`;
    }
    return "";
  };

  const getReference = () => {
    if (searchType === "case" && isCaseNotice(notice)) {
      return `Public Case Number: ${notice.caseNumber}`;
    }
    if (isDomainNotice(notice)) {
      return `Domain: ${notice.domain}`;
    }
    return "";
  };

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        onClick={onBack}
        className="flex items-center gap-2"
      >
        <ArrowLeft size={20} />
        Back to Search
      </Button>

      <Card>
        <CardHeader
          icon={<Shield size={24} />}
          title="Content Removal Notice"
          subtitle={getSubtitle()}
        />

        <CardContent className="space-y-6">
          {searchType === "domain" && isDomainNotice(notice) && (
            <InfoBox variant="error">
              <p>
                <strong>Domain Status:</strong> {notice.status}
              </p>
            </InfoBox>
          )}

          <div>
            <SectionHeader>Reason for Removal</SectionHeader>
            <p className="text-gray-700 text-sm sm:text-base">
              {notice.reason}
            </p>
          </div>

          <div>
            <SectionHeader>Legal Violations</SectionHeader>
            <ViolationList items={notice.legalViolations} bulletColor="red" />
          </div>

          <div>
            <SectionHeader>Terms of Service Violations</SectionHeader>
            <ViolationList items={notice.termsViolations} bulletColor="blue" />
          </div>

          {searchType === "domain" && isDomainNotice(notice) && (
            <InfoBox variant="gray">
              <p className="text-sm sm:text-base">
                <strong>Registry Case:</strong> {notice.caseNumber}
              </p>
              {notice.matchedUrl && (
                <p className="text-gray-600 text-sm mt-1 break-words">
                  Matched URL: {notice.matchedUrl}
                </p>
              )}
              <p className="text-gray-600 text-sm mt-1">
                Date Reported: {notice.actionDate}
              </p>
            </InfoBox>
          )}

          {searchType === "case" && isCaseNotice(notice) && (
            <InfoBox variant="gray">
              <p className="text-sm sm:text-base">
                <strong>Date Reported:</strong> {notice.dateReported}
              </p>
            </InfoBox>
          )}

          <div className="border-t border-gray-200 pt-6">
            <SectionHeader>Appeal This Decision</SectionHeader>
            <p className="text-gray-700 mb-4 text-sm sm:text-base">
              To appeal this determination, contact appeals@ncvic.org and
              reference {getReference()}, or click the Appeals button below.
            </p>
            <Button
              variant="danger"
              size="lg"
              onClick={() => setShowAppealModal(true)}
              className="w-full sm:w-auto"
            >
              Appeal this decision
            </Button>
          </div>
        </CardContent>
      </Card>

      <AppealModal
        notice={notice}
        searchType={searchType}
        isOpen={showAppealModal}
        onClose={() => setShowAppealModal(false)}
      />
    </div>
  );
}
